import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLazyGetOnboardingStatusQuery } from 'store/api/customer.apislice';
import ROUTES from 'router/routes.constants';
import {
  extractOnboardingStatusFromResponse,
  getUpgradeGateResult,
} from 'utils/investmentEligibility.utils';

/**
 * Apply the first blocking upgrade-gate action (navigate, modal, toast).
 * Used before Direct plan payment / upgrade flows.
 */
export const applyUpgradeGateAction = (gate, navigate, callbacks = {}) => {
  switch (gate.action) {
    case 'kycInReview':
      if (callbacks.onKycInReview) {
        callbacks.onKycInReview();
      } else {
        toast.error(
          'Your KYC is under review. Please wait for approval before upgrading to Direct Plan.',
        );
      }
      break;
    case 'onboarding':
      if (callbacks.onOnboardingRequired) {
        callbacks.onOnboardingRequired();
      } else {
        navigate(ROUTES.ONBOARDING_STATUS);
        toast.error(
          `Please complete your onboarding before upgrading. Incomplete: ${
            gate.incompleteSteps?.join(', ') || 'Onboarding'
          }`,
        );
      }
      break;
    case 'kyc':
      if (callbacks.onOnboardingRequired) {
        callbacks.onOnboardingRequired();
      } else {
        navigate(ROUTES.ONBOARDING_STATUS);
        toast.error('Please complete your KYC before upgrading to Direct Plan.');
      }
      break;
    case 'risk':
      if (callbacks.onRiskRequired) {
        callbacks.onRiskRequired();
      } else {
        window.dispatchEvent(new Event('open-risk-assessment-modal'));
      }
      break;
    default:
      break;
  }
};

/** Route the user to the next paid-Direct journey step (risk, payment, agreement). */
export const applyDirectJourneyStepAction = (action, navigate, callbacks = {}) => {
  switch (action) {
    case 'risk':
      if (callbacks.onRiskRequired) {
        callbacks.onRiskRequired();
      } else {
        window.dispatchEvent(new Event('open-risk-assessment-modal'));
      }
      break;
    case 'payment':
      callbacks.onPayment?.();
      break;
    case 'agreement':
      callbacks.onAgreement?.();
      break;
    default:
      break;
  }
};

/**
 * Central upgrade gate for Direct plan payment.
 * Always refetches onboarding-status on each guard check (no stale snapshots).
 */
export const useUpgradeGate = () => {
  const navigate = useNavigate();
  const [fetchOnboardingStatus, { isFetching }] =
    useLazyGetOnboardingStatusQuery();

  const fetchFreshOnboardingStatus = useCallback(async () => {
    const response = await fetchOnboardingStatus(undefined, {
      preferCacheValue: false,
    }).unwrap();

    return extractOnboardingStatusFromResponse(response);
  }, [fetchOnboardingStatus]);

  const checkUpgradeGate = useCallback(async () => {
    const onboardingStatus = await fetchFreshOnboardingStatus();
    const gate = getUpgradeGateResult(onboardingStatus);

    return { gate, onboardingStatus };
  }, [fetchFreshOnboardingStatus]);

  /**
   * Runs `proceed` only when onboarding is fully complete.
   * Returns true when the upgrade action proceeded.
   */
  const guardUpgrade = useCallback(
    async (proceed, options = {}) => {
      try {
        const { gate } = await checkUpgradeGate();

        if (!gate.allowed) {
          applyUpgradeGateAction(gate, navigate, options);
          return false;
        }

        await proceed();
        return true;
      } catch (error) {
        console.error('Error checking onboarding status for upgrade:', error);
        toast.error(
          error?.data?.message ||
            error?.message ||
            'Failed to check onboarding status',
        );
        navigate(ROUTES.ONBOARDING_STATUS);
        return false;
      }
    },
    [checkUpgradeGate, navigate],
  );

  return {
    guardUpgrade,
    checkUpgradeGate,
    isCheckingUpgradeGate: isFetching,
  };
};
